/**
 * Practice log - tracks total practice time per day
 * Stored in localStorage: { [date: string]: number } where date is YYYY-MM-DD, value is seconds
 */
import { PracticePlanItem } from './practicePlan';

const PRACTICE_LOG_KEY = 'practice-timer-log';

export type WeekStartsOn = 'monday' | 'sunday';

export interface DailyLog {
  date: string; // YYYY-MM-DD
  seconds: number;
}

export interface WeekGroup {
  weekStart: string; // YYYY-MM-DD of the week's start day
  weekLabel: string; // e.g. "Week of Mon Jan 6"
  days: DailyLog[];
}

/**
 * Helper to get local date string YYYY-MM-DD
 * This avoids the UTC shift from toISOString()
 */
function getLocalYMD(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getPracticeLog(): Record<string, number> {
  try {
    const stored = localStorage.getItem(PRACTICE_LOG_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return {};
  } catch {
    return {};
  }
}

export function addPracticeTime(seconds: number, now: number = Date.now()): void {
  const date = getLocalYMD(new Date(now)); // Use local date
  const log = getPracticeLog();
  log[date] = (log[date] ?? 0) + seconds;
  try {
    localStorage.setItem(PRACTICE_LOG_KEY, JSON.stringify(log));
  } catch (e) {
    console.error('Failed to save practice log:', e);
  }
}

export function getDailyBreakdown(): DailyLog[] {
  const log = getPracticeLog();
  return Object.entries(log)
    .map(([date, seconds]) => ({ date, seconds }))
    .sort((a, b) => b.date.localeCompare(a.date));
}

/** Get the week start date (YYYY-MM-DD) for a given date based on weekStartsOn */
function getWeekStart(dateStr: string, weekStartsOn: WeekStartsOn): string {
  // Parse as local noon to avoid midnight offsets
  const d = new Date(dateStr + 'T12:00:00');
  const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const daysSinceWeekStart =
    weekStartsOn === 'monday' ? (day + 6) % 7 : day;

  const weekStart = new Date(d);
  weekStart.setDate(d.getDate() - daysSinceWeekStart);

  // Return local YMD, not UTC
  return getLocalYMD(weekStart);
}

/** Format week start date for display, e.g. "Week of Mon Jan 6" */
function formatWeekLabel(weekStartStr: string): string {
  const d = new Date(weekStartStr + 'T12:00:00');
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const monthDay = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `Week of ${weekday} ${monthDay}`;
}

export function getDailyBreakdownByWeek(
  weekStartsOn: WeekStartsOn = 'monday',
  limitDays?: number
): WeekGroup[] {
  let dailyLogs = getDailyBreakdown();

  if (limitDays && limitDays > 0) {
    dailyLogs = dailyLogs.slice(0, limitDays);
  }

  const weekMap = new Map<string, DailyLog[]>();

  for (const log of dailyLogs) {
    const weekStart = getWeekStart(log.date, weekStartsOn);
    const existing = weekMap.get(weekStart) ?? [];
    existing.push(log);
    weekMap.set(weekStart, existing);
  }

  return Array.from(weekMap.entries())
    .sort(([a], [b]) => b.localeCompare(a)) // Sort weeks descending
    .map(([weekStart, days]) => ({
      weekStart,
      weekLabel: formatWeekLabel(weekStart),
      days,
    }));
}

export function getTotalSeconds(): number {
  const log = getPracticeLog();
  return Object.values(log).reduce((sum, s) => sum + s, 0);
}

/** Get practice time for today (YYYY-MM-DD) in seconds */
export function getTodaySeconds(): number {
  const today = getLocalYMD(); // Local
  const log = getPracticeLog();
  return log[today] ?? 0;
}

/** Get practice time for the current week in seconds (week boundaries from weekStartsOn) */
export function getThisWeekSeconds(weekStartsOn: WeekStartsOn = 'monday'): number {
  const today = getLocalYMD(); // Local
  const currentWeekStart = getWeekStart(today, weekStartsOn);
  const log = getPracticeLog();
  return Object.entries(log).reduce((sum, [date, seconds]) => {
    if (getWeekStart(date, weekStartsOn) === currentWeekStart) {
      return sum + seconds;
    }
    return sum;
  }, 0);
}

/** Get practice time for the week before the current week in seconds */
export function getLastWeekSeconds(weekStartsOn: WeekStartsOn = 'monday'): number {
  const today = getLocalYMD();
  const currentWeekStart = getWeekStart(today, weekStartsOn);
  const currentStart = new Date(currentWeekStart + 'T12:00:00');
  const lastWeekStart = new Date(currentStart);
  lastWeekStart.setDate(lastWeekStart.getDate() - 7);
  const lastWeekStartStr = getLocalYMD(lastWeekStart);
  const log = getPracticeLog();
  return Object.entries(log).reduce((sum, [date, seconds]) => {
    if (getWeekStart(date, weekStartsOn) === lastWeekStartStr) {
      return sum + seconds;
    }
    return sum;
  }, 0);
}

export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) {
    return `${h}h ${m}m`;
  }
  return `${m} min`;
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T12:00:00');
  const today = new Date();

  // To verify "Today" vs "Yesterday", we must generate local strings for comparison
  // because "d" is a generic date object.
  const todayStr = getLocalYMD(today);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = getLocalYMD(yesterday);

  if (dateStr === todayStr) return 'Today';
  if (dateStr === yesterdayStr) return 'Yesterday';

  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

const DETAILED_LOG_KEY = 'practice-timer-detailed-log';

export interface DetailedLogEntry {
  itemId: string;
  itemName: string;
  seconds: number;
}

export type DetailedPracticeLog = Record<string, Record<string, DetailedLogEntry>>;

export function getDetailedPracticeLog(): DetailedPracticeLog {
  try {
    const stored = localStorage.getItem(DETAILED_LOG_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

export function saveDetailedPracticeLog(log: DetailedPracticeLog): void {
  try {
    localStorage.setItem(DETAILED_LOG_KEY, JSON.stringify(log));
  } catch (e) {
    console.error('Failed to save detailed log:', e);
  }
}

export function addDetailedPracticeTime(
  itemId: string,
  itemName: string,
  seconds: number,
  now: number = Date.now()
): void {
  const date = getLocalYMD(new Date(now));

  // 1. Log overall time
  addPracticeTime(seconds, now);

  // 2. Log detailed time
  const log = getDetailedPracticeLog();
  if (!log[date]) {
    log[date] = {};
  }
  if (!log[date][itemId]) {
    log[date][itemId] = { itemId, itemName, seconds: 0 };
  }
  log[date][itemId].seconds += seconds;
  log[date][itemId].itemName = itemName; // Keep name in sync
  saveDetailedPracticeLog(log);
}

export interface PieceTimeSummary {
  itemId: string;
  itemName: string;
  seconds: number;
  allocatedTime?: number;
  allocationPeriod?: 'day' | 'week';
}

export type SegmentTimeSummary = PieceTimeSummary;

function flattenPlan(items: PracticePlanItem[]): PracticePlanItem[] {
  let result: PracticePlanItem[] = [];
  for (const item of items) {
    result.push(item);
    if (item.children && item.children.length > 0) {
      result = result.concat(flattenPlan(item.children));
    }
  }
  return result;
}

export function getPieceTimeForRange(
  startDateStr: string,
  endDateStr: string,
  planItems: PracticePlanItem[]
): PieceTimeSummary[] {
  const log = getDetailedPracticeLog();
  const summaryMap: Record<string, { itemName: string; seconds: number }> = {};

  // Parse ranges
  const start = new Date(startDateStr + 'T00:00:00');
  const end = new Date(endDateStr + 'T23:59:59');

  // Gather actual seconds from logs
  for (const [dateStr, pieces] of Object.entries(log)) {
    const d = new Date(dateStr + 'T12:00:00');
    if (d >= start && d <= end) {
      for (const [itemId, entry] of Object.entries(pieces)) {
        if (!summaryMap[itemId]) {
          summaryMap[itemId] = { itemName: entry.itemName, seconds: 0 };
        }
        summaryMap[itemId].seconds += entry.seconds;
      }
    }
  }

  const flatItems = flattenPlan(planItems);
  const result: PieceTimeSummary[] = [];
  const addedIds = new Set<string>();

  // Add practice segments that were practiced (have timed seconds > 0)
  for (const [itemId, summary] of Object.entries(summaryMap)) {
    const planItem = flatItems.find(
      (item) => item.id === itemId || (item.text && summary.itemName && item.text.trim().toLowerCase() === summary.itemName.trim().toLowerCase())
    );
    if (planItem && planItem.blockType !== 'segment') {
      continue;
    }
    const resolvedId = planItem?.id || itemId;
    result.push({
      itemId: resolvedId,
      itemName: planItem?.text || summary.itemName,
      seconds: summary.seconds,
      allocatedTime: planItem?.allocatedTime,
      allocationPeriod: planItem?.allocationPeriod
    });
    addedIds.add(resolvedId);
    addedIds.add(itemId);
  }

  // Include practice segments from the plan (both timed and untimed segments)
  for (const planItem of flatItems) {
    if (planItem.blockType === 'segment' && !addedIds.has(planItem.id)) {
      if (planItem.text && planItem.text.trim().length > 0) {
        result.push({
          itemId: planItem.id,
          itemName: planItem.text,
          seconds: 0,
          allocatedTime: planItem.allocatedTime,
          allocationPeriod: planItem.allocationPeriod
        });
        addedIds.add(planItem.id);
      }
    }
  }

  // Include any segments with completions logged in range that were not in plan or detailed log
  const completions = getSegmentCompletions();
  const startMs = start.getTime();
  const endMs = end.getTime();
  for (const [itemId, rawTimestamps] of Object.entries(completions)) {
    if (addedIds.has(itemId)) continue;
    const planItem = flatItems.find((p) => p.id === itemId);
    if (planItem && planItem.blockType !== 'segment') {
      continue;
    }
    const itemTimestamps = deduplicateTimestamps(rawTimestamps);
    const count = itemTimestamps.filter((ts) => ts >= startMs && ts <= endMs).length;
    if (count > 0) {
      result.push({
        itemId,
        itemName: planItem?.text || getItemPlanName(itemId) || 'Practice Segment',
        seconds: 0,
        allocatedTime: planItem?.allocatedTime,
        allocationPeriod: planItem?.allocationPeriod
      });
      addedIds.add(itemId);
    }
  }

  return result;
}

export const getSegmentTimeForRange = getPieceTimeForRange;

export function getThisWeekRange(weekStartsOn: WeekStartsOn = 'monday'): { start: string; end: string } {
  const start = getWeekStart(getLocalYMD(), weekStartsOn);
  const startD = new Date(start + 'T12:00:00');
  const endD = new Date(startD);
  endD.setDate(startD.getDate() + 6);
  return { start, end: getLocalYMD(endD) };
}

export function getLastWeekRange(weekStartsOn: WeekStartsOn = 'monday'): { start: string; end: string } {
  const thisWeekStart = getWeekStart(getLocalYMD(), weekStartsOn);
  const thisWeekStartD = new Date(thisWeekStart + 'T12:00:00');
  const startD = new Date(thisWeekStartD);
  startD.setDate(startD.getDate() - 7);
  const endD = new Date(startD);
  endD.setDate(startD.getDate() + 6);
  return { start: getLocalYMD(startD), end: getLocalYMD(endD) };
}

export function getPiecePracticedSeconds(
  itemId: string,
  period: 'day' | 'week',
  weekStartsOn: WeekStartsOn = 'monday'
): number {
  const log = getDetailedPracticeLog();
  let totalSeconds = 0;

  if (period === 'day') {
    const today = getLocalYMD();
    return log[today]?.[itemId]?.seconds ?? 0;
  } else {
    const { start, end } = getThisWeekRange(weekStartsOn);
    const startDate = new Date(start + 'T00:00:00');
    const endDate = new Date(end + 'T23:59:59');

    for (const [dateStr, pieces] of Object.entries(log)) {
      const d = new Date(dateStr + 'T12:00:00');
      if (d >= startDate && d <= endDate) {
        totalSeconds += pieces[itemId]?.seconds ?? 0;
      }
    }
  }

  return totalSeconds;
}

export interface Last7DaysSummary {
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
  totalSeconds: number;
  pieces: Array<{ itemId: string; itemName: string; seconds: number; completionsCount?: number }>;
}

export function getReportSummaryForRange(
  startDate: string,
  endDate: string,
  planItems: PracticePlanItem[]
): Last7DaysSummary {
  const pieceMap: Record<string, { itemName: string; seconds: number }> = {};
  const log = getDetailedPracticeLog();
  const overallLog = getPracticeLog();

  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T23:59:59');

  let totalSeconds = 0;
  for (const [dateStr, secs] of Object.entries(overallLog)) {
    const d = new Date(dateStr + 'T12:00:00');
    if (d >= start && d <= end) totalSeconds += secs;
  }

  for (const [dateStr, pieces] of Object.entries(log)) {
    const d = new Date(dateStr + 'T12:00:00');
    if (d >= start && d <= end) {
      for (const [itemId, entry] of Object.entries(pieces)) {
        if (!pieceMap[itemId]) pieceMap[itemId] = { itemName: entry.itemName, seconds: 0 };
        pieceMap[itemId].seconds += entry.seconds;
      }
    }
  }

  const detailedTotalSeconds = Object.values(pieceMap).reduce((sum, item) => sum + item.seconds, 0);
  if (detailedTotalSeconds > totalSeconds) {
    totalSeconds = detailedTotalSeconds;
  }

  const completions = getSegmentCompletions();
  const startMs = start.getTime();
  const endMs = end.getTime();

  for (const [itemId, rawTimestamps] of Object.entries(completions)) {
    const itemTimestamps = deduplicateTimestamps(rawTimestamps);
    const count = itemTimestamps.filter((ts) => ts >= startMs && ts <= endMs).length;
    if (count > 0 && !pieceMap[itemId]) {
      const flat = flattenPlan(planItems);
      const planItem = flat.find((p) => p.id === itemId);
      pieceMap[itemId] = { itemName: planItem?.text || 'Segment', seconds: 0 };
    }
  }

  const pieces = Object.entries(pieceMap)
    .map(([itemId, { itemName, seconds }]) => {
      const explicitCount = getSegmentCompletionsForRange(itemId, startDate, endDate);

      return {
        itemId,
        itemName,
        seconds,
        completionsCount: explicitCount,
      };
    })
    .sort((a, b) => b.seconds - a.seconds);

  return { startDate, endDate, totalSeconds, pieces };
}

export function getThisWeekSummary(
  planItems: PracticePlanItem[],
  weekStartsOn: WeekStartsOn = 'monday'
): Last7DaysSummary {
  const { start, end } = getThisWeekRange(weekStartsOn);
  return getReportSummaryForRange(start, end, planItems);
}

export function getLastWeekSummary(
  planItems: PracticePlanItem[],
  weekStartsOn: WeekStartsOn = 'monday'
): Last7DaysSummary {
  const { start, end } = getLastWeekRange(weekStartsOn);
  return getReportSummaryForRange(start, end, planItems);
}

export function getLast7DaysSummary(planItems: PracticePlanItem[]): Last7DaysSummary {
  const endDate = getLocalYMD();
  const endD = new Date(endDate + 'T12:00:00');
  const startD = new Date(endD);
  startD.setDate(startD.getDate() - 6);
  const startDate = getLocalYMD(startD);
  return getReportSummaryForRange(startDate, endDate, planItems);
}

const PRACTICE_COMPLETIONS_KEY = 'practice-timer-completions';

export type SegmentCompletionLog = Record<string, number[]>;

export function saveSegmentCompletions(log: SegmentCompletionLog): void {
  try {
    localStorage.setItem(PRACTICE_COMPLETIONS_KEY, JSON.stringify(log));
  } catch (e) {
    console.error('Failed to save segment completions:', e);
  }
}

function deduplicateTimestamps(timestamps: number[]): number[] {
  if (!timestamps || timestamps.length === 0) return [];
  const sorted = [...timestamps].sort((a, b) => a - b);
  const result: number[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] - result[result.length - 1] >= 15000) {
      result.push(sorted[i]);
    }
  }
  return result;
}

export function getSegmentCompletions(): SegmentCompletionLog {
  try {
    const stored = localStorage.getItem(PRACTICE_COMPLETIONS_KEY);
    if (!stored) return {};
    const parsed: SegmentCompletionLog = JSON.parse(stored);
    let needsSave = false;
    const cleaned: SegmentCompletionLog = {};
    for (const [id, timestamps] of Object.entries(parsed)) {
      if (Array.isArray(timestamps)) {
        const deduped = deduplicateTimestamps(timestamps);
        cleaned[id] = deduped;
        if (deduped.length !== timestamps.length) {
          needsSave = true;
        }
      }
    }
    if (needsSave) {
      try {
        localStorage.setItem(PRACTICE_COMPLETIONS_KEY, JSON.stringify(cleaned));
      } catch (e) {
        console.error('Failed to clean stored completion timestamps:', e);
      }
    }
    return cleaned;
  } catch {
    return {};
  }
}

export function logSegmentCompletion(itemId: string, timestamp: number = Date.now()): void {
  if (!itemId) return;
  const completions = getSegmentCompletions();
  if (!completions[itemId]) {
    completions[itemId] = [];
  }
  const lastTs = completions[itemId][completions[itemId].length - 1];
  if (lastTs && Math.abs(timestamp - lastTs) < 15000) {
    return;
  }
  completions[itemId].push(timestamp);
  saveSegmentCompletions(completions);
}

export function removeSegmentCompletionToday(itemId: string, now: number = Date.now()): void {
  if (!itemId) return;
  const completions = getSegmentCompletions();
  const rawTimestamps = completions[itemId] || [];
  if (rawTimestamps.length === 0) return;

  const dateObj = new Date(now);
  const dateStr = getLocalYMD(dateObj);
  const startMs = new Date(dateStr + 'T00:00:00').getTime();
  const endMs = startMs + 24 * 60 * 60 * 1000;

  let lastIndexToday = -1;
  for (let i = rawTimestamps.length - 1; i >= 0; i--) {
    if (rawTimestamps[i] >= startMs && rawTimestamps[i] < endMs) {
      lastIndexToday = i;
      break;
    }
  }

  if (lastIndexToday !== -1) {
    rawTimestamps.splice(lastIndexToday, 1);
    completions[itemId] = rawTimestamps;
    saveSegmentCompletions(completions);
  }
}

function getAllItemTimestamps(itemId: string, completions: SegmentCompletionLog): number[] {
  const ids = new Set<string>([itemId]);
  try {
    const rawPractice = localStorage.getItem('practice-timer-plan');
    if (rawPractice) {
      const items: PracticePlanItem[] = JSON.parse(rawPractice);
      const flat = flattenPlan(items);
      const current = flat.find((i) => i.id === itemId);
      if (current?.text) {
        const targetText = current.text.trim().toLowerCase();
        for (const i of flat) {
          if (i.text && i.text.trim().toLowerCase() === targetText) {
            ids.add(i.id);
          }
        }
      }
    }
  } catch {
    // ignore
  }
  const combined: number[] = [];
  ids.forEach((id) => {
    if (Array.isArray(completions[id])) {
      combined.push(...completions[id]);
    }
  });
  return deduplicateTimestamps(combined);
}

function getItemPlanName(itemId: string): string | undefined {
  try {
    const rawPractice = localStorage.getItem('practice-timer-plan');
    if (rawPractice) {
      const items = flattenPlan(JSON.parse(rawPractice));
      const match = items.find((i) => i.id === itemId);
      if (match?.text) return match.text;
    }
    const rawLesson = localStorage.getItem('lesson-plan-items');
    if (rawLesson) {
      const items = flattenPlan(JSON.parse(rawLesson));
      const match = items.find((i) => i.id === itemId);
      if (match?.text) return match.text;
    }
  } catch {
    // ignore
  }
  return undefined;
}

function getSecondsForDate(
  pieces: Record<string, { itemId: string; itemName: string; seconds: number }>,
  itemId: string,
  targetName?: string
): { seconds: number; itemName?: string } {
  let seconds = 0;
  let itemName: string | undefined;
  const lowerTarget = targetName?.trim().toLowerCase();

  for (const [id, entry] of Object.entries(pieces)) {
    const matchId = id === itemId;
    const matchName = Boolean(lowerTarget && entry.itemName && entry.itemName.trim().toLowerCase() === lowerTarget);
    if (matchId || matchName) {
      seconds += entry.seconds ?? 0;
      if (!itemName && entry.itemName) itemName = entry.itemName;
    }
  }

  return { seconds, itemName };
}

export function getSegmentCompletionsForRange(
  itemId: string,
  startDateStr: string,
  endDateStr: string
): number {
  const completions = getSegmentCompletions();
  const itemTimestamps = getAllItemTimestamps(itemId, completions);

  const start = new Date(startDateStr + 'T00:00:00');
  const end = new Date(endDateStr + 'T23:59:59');
  const startMs = start.getTime();
  const endMs = end.getTime();

  return itemTimestamps.filter((ts) => ts >= startMs && ts <= endMs).length;
}

export function getSegmentCompletionsForThisWeek(
  itemId: string,
  weekStartsOn: WeekStartsOn = 'monday',
  now: number = Date.now()
): number {
  const dateObj = new Date(now);
  const dateStr = getLocalYMD(dateObj);
  const weekStart = getWeekStart(dateStr, weekStartsOn);
  const startD = new Date(weekStart + 'T12:00:00');
  const endD = new Date(startD);
  endD.setDate(startD.getDate() + 6);
  return getSegmentCompletionsForRange(itemId, weekStart, getLocalYMD(endD));
}

export function getSegmentCompletionsToday(
  itemId: string,
  now: number = Date.now()
): number {
  const completions = getSegmentCompletions();
  const itemTimestamps = getAllItemTimestamps(itemId, completions);

  const dateObj = new Date(now);
  const dateStr = getLocalYMD(dateObj);
  const startMs = new Date(dateStr + 'T00:00:00').getTime();
  const endMs = startMs + 24 * 60 * 60 * 1000;

  return itemTimestamps.filter((ts) => ts >= startMs && ts < endMs).length;
}

export function hasCompletedSegmentToday(
  itemId: string,
  now: number = Date.now()
): boolean {
  return getSegmentCompletionsToday(itemId, now) > 0;
}

export function hasPlayedSegmentInLast2Days(
  itemId: string,
  now: number = Date.now()
): boolean {
  if (!itemId) return false;

  const dateObj = new Date(now);
  const todayStr = getLocalYMD(dateObj);

  const yesterdayObj = new Date(dateObj);
  yesterdayObj.setDate(yesterdayObj.getDate() - 1);
  const yesterdayStr = getLocalYMD(yesterdayObj);

  const startYesterdayMs = new Date(yesterdayStr + 'T00:00:00').getTime();
  const endTodayMs = new Date(todayStr + 'T23:59:59').getTime();

  const completions = getSegmentCompletions();
  const itemTimestamps = getAllItemTimestamps(itemId, completions);

  const hasTimestamp = itemTimestamps.some((ts) => ts >= startYesterdayMs && ts <= endTodayMs);
  if (hasTimestamp) return true;

  const detailedLog = getDetailedPracticeLog();
  const targetName = getItemPlanName(itemId);
  const todaySecs = getSecondsForDate(detailedLog[todayStr] || {}, itemId, targetName).seconds;
  const yesterdaySecs = getSecondsForDate(detailedLog[yesterdayStr] || {}, itemId, targetName).seconds;
  if (todaySecs >= 60 || yesterdaySecs >= 60) return true;

  return false;
}

export function getSegmentLastPlayed(
  itemId: string,
  now: number = Date.now()
): number | null {
  if (!itemId) return null;

  const completions = getSegmentCompletions();
  const itemTimestamps = getAllItemTimestamps(itemId, completions);

  let latestMs: number | null = null;
  for (const ts of itemTimestamps) {
    if (ts <= now && (latestMs === null || ts > latestMs)) {
      latestMs = ts;
    }
  }

  // Also check detailed log entries matching itemId or piece name
  const detailedLog = getDetailedPracticeLog();
  const rawPractice = localStorage.getItem('practice-timer-plan');
  let itemName: string | undefined;
  if (rawPractice) {
    try {
      const items = flattenPlan(JSON.parse(rawPractice));
      itemName = items.find((i) => i.id === itemId)?.text;
    } catch {
      // ignore
    }
  }

  const targetName = itemName?.trim().toLowerCase();

  for (const [dateStr, pieces] of Object.entries(detailedLog)) {
    for (const [id, entry] of Object.entries(pieces)) {
      const matchId = id === itemId;
      const matchName = Boolean(targetName && entry.itemName && entry.itemName.trim().toLowerCase() === targetName);
      if ((matchId || matchName) && entry.seconds > 0) {
        const isToday = dateStr === getLocalYMD(new Date(now));
        const dayMs = isToday ? now : new Date(dateStr + 'T12:00:00').getTime();
        if (dayMs <= now && (latestMs === null || dayMs > latestMs)) {
          latestMs = dayMs;
        }
      }
    }
  }

  return latestMs;
}

export function formatLastPlayed(lastPlayedMs: number | null, now: number = Date.now()): string {
  if (lastPlayedMs === null || lastPlayedMs <= 0) return 'Never';

  const dateObj = new Date(lastPlayedMs);
  const nowObj = new Date(now);

  const playedDateStr = getLocalYMD(dateObj);
  const todayStr = getLocalYMD(nowObj);

  const yesterdayObj = new Date(nowObj);
  yesterdayObj.setDate(yesterdayObj.getDate() - 1);
  const yesterdayStr = getLocalYMD(yesterdayObj);

  if (playedDateStr === todayStr) return 'Today';
  if (playedDateStr === yesterdayStr) return 'Yesterday';

  const todayStart = new Date(todayStr + 'T00:00:00').getTime();
  const playedStart = new Date(playedDateStr + 'T00:00:00').getTime();
  const diffDays = Math.round((todayStart - playedStart) / (24 * 3600 * 1000));

  if (diffDays >= 2 && diffDays <= 6) {
    return `${diffDays}d ago`;
  }

  const isSameYear = dateObj.getFullYear() === nowObj.getFullYear();
  return dateObj.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(isSameYear ? {} : { year: 'numeric' }),
  });
}

export function getCompletionPillColorClass(
  completionsCount: number,
  playedInLast2Days: boolean
): string {
  if (completionsCount >= 5) {
    return "bg-emerald-500/15 border-emerald-500/35 text-emerald-700 dark:text-emerald-300";
  }
  if (!playedInLast2Days) {
    return "bg-amber-500/15 border-amber-500/35 text-amber-700 dark:text-amber-300";
  }
  return "bg-muted/60 border-muted-foreground/20 text-muted-foreground";
}

export function getSegmentCompletionsLast7Days(
  itemId: string,
  now: number = Date.now()
): number {
  const completions = getSegmentCompletions();
  const rawTimestamps = completions[itemId] || [];
  const itemTimestamps = deduplicateTimestamps(rawTimestamps);

  const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
  return itemTimestamps.filter((ts) => ts >= sevenDaysAgo && ts <= now).length;
}

export function getAllSegmentCompletionsForThisWeek(
  weekStartsOn: WeekStartsOn = 'monday',
  now: number = Date.now()
): Record<string, number> {
  const completions = getSegmentCompletions();
  const result: Record<string, number> = {};
  for (const itemId of Object.keys(completions)) {
    result[itemId] = getSegmentCompletionsForThisWeek(itemId, weekStartsOn, now);
  }
  return result;
}

export function getPracticeLogStateForSync(): {
  log: Record<string, number>;
  detailedLog: DetailedPracticeLog;
  completions: SegmentCompletionLog;
} {
  return {
    log: getPracticeLog(),
    detailedLog: getDetailedPracticeLog(),
    completions: getSegmentCompletions(),
  };
}

export function restorePracticeLogStateFromSync(data: {
  log?: Record<string, number>;
  detailedLog?: DetailedPracticeLog;
  completions?: SegmentCompletionLog;
}): void {
  if (data.log) {
    const currentLog = getPracticeLog();
    const mergedLog = { ...data.log };
    for (const [date, secs] of Object.entries(currentLog)) {
      mergedLog[date] = Math.max(mergedLog[date] ?? 0, secs);
    }
    try {
      localStorage.setItem(PRACTICE_LOG_KEY, JSON.stringify(mergedLog));
    } catch (e) {
      console.error('Failed to restore practice log:', e);
    }
  }
  if (data.detailedLog) {
    const currentDetailed = getDetailedPracticeLog();
    const mergedDetailed = { ...data.detailedLog };
    for (const [date, items] of Object.entries(currentDetailed)) {
      if (!mergedDetailed[date]) {
        mergedDetailed[date] = items;
      } else {
        mergedDetailed[date] = { ...mergedDetailed[date] };
        for (const [itemId, entry] of Object.entries(items)) {
          if (!mergedDetailed[date][itemId]) {
            mergedDetailed[date][itemId] = entry;
          } else {
            mergedDetailed[date][itemId] = {
              ...mergedDetailed[date][itemId],
              seconds: Math.max(mergedDetailed[date][itemId].seconds, entry.seconds),
              itemName: entry.itemName || mergedDetailed[date][itemId].itemName,
            };
          }
        }
      }
    }
    saveDetailedPracticeLog(mergedDetailed);
  }
  if (data.completions) {
    const currentCompletions = getSegmentCompletions();
    const mergedCompletions = { ...data.completions };
    for (const [itemId, timestamps] of Object.entries(currentCompletions)) {
      if (!mergedCompletions[itemId]) {
        mergedCompletions[itemId] = timestamps;
      } else {
        const combined = Array.from(new Set([...mergedCompletions[itemId], ...timestamps])).sort((a, b) => a - b);
        mergedCompletions[itemId] = combined;
      }
    }
    saveSegmentCompletions(mergedCompletions);
  }
}


